import React from 'react';
import { NewsListingStyles as S } from './NewsListing.styles';
import withWidget from '@hoc/withWidget';
import { HydrateOption } from '@core/enums';
import { Grid } from '@helpers/grid';
import { ArrayParam, NumberParam, StringParam, useQueryParam, useQueryParams, withDefault, } from 'use-query-params';
import { withQueryParams } from '@hoc/withQueryParams';
import { ReactComponent as ChevronDown } from '@img/icons/chevron-down.svg';
import { ReactComponent as Chevron } from '@img/icons/chevron-left.svg';
import { ReactComponent as Tick } from '@img/icons/tick.svg';
import { ReactComponent as Close } from '@img/icons/close.svg';
import { withMotion } from '@hoc/withMotion';
import { useClickAway, useLockBodyScroll, useUpdateEffect } from 'react-use';
import ApiService from '@core/api/services/ApiService';
import { AnimatePresence } from 'framer-motion';
import { PuffLoader } from 'react-spinners';
import { formatDateLong } from '@helpers/date';
import { useInView } from '@hooks/useInView';
import Button, { LinkButton } from '@stories/Components/Buttons/Button/Button';
import { useSelector } from 'react-redux';
import UrlResolver from '@core/url-resolver/UrlResolver';
import withRedux from '@helpers/withRedux';
const MultiSelectFilter = (props) => {
    const [expanded, setExpanded] = React.useState(false);
    const ref = React.useRef(null);
    useClickAway(ref, () => setExpanded(false));
    const wrapperRef = React.useRef(null);
    const multiSelectOptionsRef = React.useRef(null);
    React.useEffect(() => {
        if (multiSelectOptionsRef.current && wrapperRef.current && expanded) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            multiSelectOptionsRef.current.style.maxHeight = `calc(100dvh - ${wrapperRect.top + wrapperRect.height}px)`;
        }
    }, [expanded]);
    return (React.createElement(S.MultiSelect, { "data-disabled": props.disabled, ref: ref },
        React.createElement(S.MultiSelectWrapper, { ref: wrapperRef, "aria-expanded": expanded, onClick: () => setExpanded(!expanded) },
            React.createElement("span", null, props.label),
            React.createElement(ChevronDown, null)),
        React.createElement(S.MultiSelectOptions, { ref: multiSelectOptionsRef, "data-visible": expanded }, props.options &&
            props.options.map((option) => (React.createElement("div", { key: option.id, onClick: () => !props.disabled && props.onClick(option.id) },
                React.createElement(S.MultiSelectCheck, { "data-selected": props.singleSelectMode
                        ? props.params === option.id
                        : props.params?.includes(option.id) },
                    React.createElement(Tick, null)),
                React.createElement("span", null, option.value)))))));
};
const NewsListingCard = (props) => {
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const imageRef = React.useRef(null);
    const [ref, inView] = useInView({ threshold: 0.25 });
    const [hovering, setHovering] = React.useState(false);
    React.useEffect(() => {
        // Handle scenario where image is cached //
        if (imageRef.current?.complete) {
            setImageLoaded(true);
        }
    }, [imageRef]);
    return (React.createElement(S.CardWrapper, { href: props.url, target: props.isVideo ? "_blank" : "_self", onMouseEnter: () => setHovering(true), onMouseLeave: () => setHovering(false), "data-in-view": inView, ref: ref },
        React.createElement(S.CardInner, null,
            React.createElement(S.Date, null, formatDateLong(props.date, true)),
            React.createElement(S.ImageWrapper, null,
                React.createElement(S.HoverEffect, { "data-active": hovering }),
                React.createElement(AnimatePresence, null, !imageLoaded && (React.createElement(S.SpinnerContainer, null,
                    React.createElement(PuffLoader, null)))),
                React.createElement(S.LazyImage, { "data-loaded": imageLoaded, src: props.image, alt: props.title, loading: "lazy", ref: imageRef, onLoad: () => setImageLoaded(true) })),
            React.createElement(S.CardContent, null,
                React.createElement(S.CardContentLine, null),
                React.createElement(S.CardContentTitle, null, props.title),
                React.createElement(S.CardContentIntro, null, props.intro),
                !props.isVideo && (React.createElement(S.CardContentCta, null,
                    React.createElement("span", null, props.readMoreText),
                    " ",
                    React.createElement(Chevron, null)))))));
};
const PAGE_SIZE = 8;
const NewsListing = (props) => {
    const [params, setParams] = useQueryParams({
        ...props.filters.reduce((acc, filter) => {
            acc[filter.id] = withDefault(ArrayParam, []);
            return acc;
        }, {}),
    });
    const cultureCode = useSelector((x) => x.pageContext.documentCulture);
    const [pageIndex, setPageIndex] = useQueryParam('pageIndex', withDefault(NumberParam, props.pageIndex));
    const [sortBy, setSortBy] = useQueryParam('sortBy', withDefault(StringParam, undefined));
    const selectedOptions = React.useMemo(() => {
        return props.filters.reduce((acc, filter) => {
            acc[filter.id] = filter.options.filter((option) => params[filter.id].includes(option.id));
            return acc;
        }, {});
    }, [params, props.filters]);
    const handleMultiSelectOptionSelect = (filterId, optionId, isSingle) => {
        isSingle
            ? sortBy === optionId
                ? setSortBy(undefined)
                : setSortBy(optionId)
            : setParams({
                [filterId]: params[filterId].includes(optionId)
                    ? params[filterId].filter((id) => id !== optionId)
                    : [...params[filterId], optionId],
            });
    };
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState(props.initialData);
    const [totalResults, setTotalResults] = React.useState(props.totalPages * PAGE_SIZE);
    const previousPageRef = React.useRef(pageIndex);
    const [showMobileFilters, setShowMobileFilters] = React.useState(false);
    const [activeMobileFilter, setActiveMobileFilter] = React.useState(null);
    const [hasMore, setHasMore] = React.useState(props.initialData ? props.pageIndex < props.totalPages : true);
    const scrollableSectionRef = React.useRef(null);
    useLockBodyScroll(showMobileFilters, scrollableSectionRef);
    const handleParamChange = async () => {
        setLoading(true);
        setParams({ pageIndex: 1 });
        const request = {
            newsDetailPage: props.newsDetailPage,
            pageIndex: pageIndex,
            sortBy,
            cultureCode,
            pageSize: PAGE_SIZE,
            filters: props.filters.reduce((acc, filter) => {
                acc[filter.id] = selectedOptions[filter.id]?.map((option) => option.id);
                return acc;
            }, {}),
        };
        const response = await ApiService.request({
            baseUrl: UrlResolver.getDomain(),
            method: 'POST',
            controller: 'news',
            slug: 'list',
            params: request,
        });
        if (response.status?.success) {
            setData(response.result);
            setHasMore(response.pageIndex < response.totalPages);
            setTotalResults(response.totalResultCount);
        }
        setTimeout(() => {
            setLoading(false);
        }, 750);
    };
    const handleNextPage = async () => {
        setLoading(true);
        if (pageIndex === previousPageRef.current) {
            return;
        }
        previousPageRef.current = pageIndex;
        const request = {
            newsDetailPage: props.newsDetailPage,
            pageIndex,
            sortBy,
            cultureCode,
            pageSize: PAGE_SIZE,
            filters: props.filters.reduce((acc, filter) => {
                acc[filter.id] = selectedOptions[filter.id]?.map((option) => option.id);
                return acc;
            }, {}),
        };
        const response = await ApiService.request({
            baseUrl: UrlResolver.getDomain(),
            method: 'POST',
            controller: 'news',
            slug: 'list',
            params: request,
        });
        if (response.status.success) {
            setData([...data, ...response.result]);
            setHasMore(response.pageIndex < response.totalPages);
            setTotalResults(response.totalResultCount);
        }
        setTimeout(() => {
            setLoading(false);
        }, 750);
    };
    useUpdateEffect(() => {
        handleNextPage();
    }, [pageIndex]);
    useUpdateEffect(() => {
        handleParamChange();
    }, [sortBy, params]);
    const Pills = React.useMemo(() => {
        return Object.keys(selectedOptions)?.map((key) => (React.createElement(React.Fragment, null, selectedOptions[key]?.map((option) => (React.createElement(S.Pill, { onClick: () => handleMultiSelectOptionSelect(key, option.id), key: `NewsListing_Pill_${key}_${option.id}` },
            option.value,
            " ",
            React.createElement(Close, null)))))));
    }, [selectedOptions]);
    return (React.createElement(Grid.Default, null,
        React.createElement(S.Container, null,
            React.createElement(S.TitleContainer, null,
                React.createElement("h1", null, props.title),
                props.intro && React.createElement("p", null, props.intro)),
            React.createElement(S.FiltersContainer, null,
                React.createElement(S.FiltersControls, null,
                    React.createElement(S.DesktopFiltersWrapper, null,
                        React.createElement(S.FiltersLeft, null,
                            React.createElement("span", null,
                                props.translations['custom.news-listing.filter'],
                                ":"),
                            props.filters &&
                                props.filters.map((filter, i) => (React.createElement(MultiSelectFilter, { key: `NewsListing_MultiSelect_${i}`, disabled: loading, ...filter, params: params[filter.id], onClick: (id) => handleMultiSelectOptionSelect(filter.id, id) })))),
                        React.createElement(S.FiltersRight, null,
                            React.createElement(MultiSelectFilter, { singleSelectMode: true, disabled: loading, ...props.sortBy, params: sortBy, onClick: (id) => handleMultiSelectOptionSelect('sortBy', id, true) }),
                            React.createElement("span", { dangerouslySetInnerHTML: {
                                    __html: props.translations['custom.news-listing.pagination']
                                        .replace('[x]', (1).toString())
                                        .replace('[y]', ((pageIndex - 1) * PAGE_SIZE + PAGE_SIZE).toString())
                                        .replace('[z]', (totalResults ?? 0).toString()),
                                } }))),
                    React.createElement(S.MobileFiltersWrapper, null,
                        React.createElement(S.MultiSelectWrapper, { "data-mobile-trigger": true, onClick: () => setShowMobileFilters(true) },
                            React.createElement("span", null, props.translations['custom.news-listing.filter']),
                            React.createElement(Chevron, null)),
                        React.createElement(MultiSelectFilter, { singleSelectMode: true, disabled: loading, ...props.sortBy, params: sortBy, onClick: (id) => handleMultiSelectOptionSelect('sortBy', id, true) }))),
                React.createElement(S.Pills, null, Pills)),
            React.createElement(S.MobilePagination, null,
                React.createElement("span", { dangerouslySetInnerHTML: {
                        __html: props.translations['custom.news-listing.pagination']
                            .replace('[x]', (1).toString())
                            .replace('[y]', ((pageIndex - 1) * PAGE_SIZE + PAGE_SIZE).toString())
                            .replace('[z]', (totalResults ?? 0).toString()),
                    } })),
            React.createElement(S.Cards, null,
                React.createElement(AnimatePresence, { exitBeforeEnter: true }, data.length ?
                    data.map((card, i) => (React.createElement(NewsListingCard, { ...card, key: `NewsListing_Card_${i}_${card.id}`, readMoreText: props.translations['custom.news-listing.read-more'] }))) : (React.createElement(React.Fragment, null,
                    React.createElement(S.NoResultsMsg, { dangerouslySetInnerHTML: { __html: props.translations['custom.news-listing.no-results'] } }))))),
            hasMore && (React.createElement(S.LoadMore, null,
                React.createElement(LinkButton, { href: `?pageIndex=${pageIndex + 1}`, branding: "primaryAlt", hoverColour: "black", title: props.translations['custom.news-listing.load-more'], onClick: (e) => {
                        e.preventDefault();
                        setPageIndex(pageIndex + 1);
                    } })))),
        React.createElement(AnimatePresence, null, showMobileFilters && (React.createElement(S.MobileFiltersContainer, null,
            React.createElement(S.MobileCloseButton, { onClick: () => setShowMobileFilters(false) },
                React.createElement(Close, null)),
            React.createElement(S.MobileFiltersInner, null,
                activeMobileFilter ? (React.createElement(React.Fragment, null,
                    React.createElement("span", { "data-is-back": true, onClick: () => setActiveMobileFilter(null) },
                        React.createElement(Chevron, null),
                        activeMobileFilter.label))) : (React.createElement("span", null, props.translations['custom.news-listing.filters'])),
                activeMobileFilter?.options ? (React.createElement(S.MobileFilterSections, { ref: scrollableSectionRef }, activeMobileFilter.options.map((option, i) => (React.createElement(S.MobileFilterOption, { "data-disabled": loading, onClick: () => handleMultiSelectOptionSelect(activeMobileFilter.id, option.id), key: `NewsListing_${activeMobileFilter.id}_MobileFilterOption_${i}` },
                    React.createElement("span", null, option.value),
                    React.createElement("div", { "data-selected": params[activeMobileFilter.id].includes(option.id) },
                        React.createElement(Tick, null))))))) : (React.createElement(React.Fragment, null,
                    React.createElement(S.Pills, null, Pills),
                    props.filters &&
                        props.filters.map((filter, i) => (React.createElement(S.MobileFilterSection, { onClick: () => setActiveMobileFilter(filter), key: `NewsListing_MobileFilters_${i}` },
                            React.createElement("span", null, filter.label),
                            React.createElement(Chevron, null)))))),
                React.createElement(S.MobileButtons, null,
                    React.createElement(Button, { onClick: () => setShowMobileFilters(false), branding: "primaryAlt", title: props.translations['custom.news-listing.view-results'] }),
                    activeMobileFilter ? (React.createElement(Button, { onClick: () => {
                            setActiveMobileFilter(null);
                        }, branding: "white", title: props.translations['custom.news-listing.back'] })) : (React.createElement(Button, { onClick: () => {
                            setParams(Object.keys(params).reduce((acc, key) => ({ ...acc, [key]: [] }), {}));
                            setSortBy(undefined);
                            setShowMobileFilters(false);
                        }, branding: "white", title: props.translations['custom.news-listing.remove-all-filters'] })))))))));
};
export default withWidget(withQueryParams(withRedux(withMotion(NewsListing))), 'NewsListing', {
    hydrate: HydrateOption.InView,
});
